import React, { useRef, useEffect } from 'react';
import classNames from 'classnames';
import Alerts from 'app/scripts/views/lib/alerts.coffee';
import {
  usePopover,
  Popover,
  PopoverScreen,
  HideReason,
  Button,
} from '@trello/nachos';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { Feature } from 'app/scripts/debug/constants';
import {
  useWorkspacesPreambleBoardHeaderButtonQuery,
  useWorkspacesPreambleDismissPromptMutation,
} from '@trello/graphql/generated';
import { WorkspacesPreambleCreateTeam } from './WorkspacesPreambleCreateTeam';
import { WorkspacesPreambleSelectTeam } from './WorkspacesPreambleSelectTeam';
import { forNamespace } from 'app/common/lib/util/i18n';
import { isBoardPath } from 'app/gamma/src/util/url';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { Analytics } from '@trello/atlassian-analytics';
import styles from './WorkspacesPreambleBoardHeaderButton.less';

const format = forNamespace();
const formatPreamble = forNamespace('workspaces preamble');

interface WorkspacesPreambleBoardHeaderButtonProps {
  boardId: string;
  canAutoShow: boolean;
  onSuccess: () => void;
  windowUrl?: string;
}

function usePrevious<T>(value: T) {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

enum Screens {
  Intro,
  SelectTeam,
  CreateTeam,
}

export const WorkspacesPreambleBoardHeaderButton: React.FunctionComponent<WorkspacesPreambleBoardHeaderButtonProps> = ({
  boardId,
  canAutoShow,
  onSuccess,
  windowUrl,
}) => {
  const { data } = useWorkspacesPreambleBoardHeaderButtonQuery({
    variables: { boardId },
  });

  const DISMISS_MESSAGE = `WorkspacesPreamblePrompt-${boardId}`;

  /**
   * For the Add to team button to be shown, the board and user must meet the following:
   * - The board does not belong to a team
   * - The board has at least 2 confirmed members
   * - The user is a board admin
   * - The user is not a member of an enterprise team
   */
  const meId = data?.member?.id;
  const meMembership = data?.board?.memberships?.find(
    (membership) => membership.idMember === meId,
  );
  const isBoardAdmin = meMembership?.memberType === 'admin';
  const isEnterpriseMember = !!data?.member?.enterprises?.length;
  const isTeamless = !data?.board?.idOrganization;
  const numberOfConfirmedCollaborators = data?.board?.members?.reduce(
    (count, member) => (member.confirmed ? count + 1 : count),
    0,
  );
  const hasEnoughCollaborators =
    numberOfConfirmedCollaborators && numberOfConfirmedCollaborators >= 2;
  const boardAndMemberMeetRequirements = !!(
    isBoardAdmin &&
    isTeamless &&
    hasEnoughCollaborators &&
    !isEnterpriseMember
  );
  const isMemberOfTeam = data?.member?.idOrganizations?.length || 0;

  /**
   * Prompt should only show automatically if:
   * - The member's oneTimeMessagesDismissed does not include a dismiss message for this board
   * - The page is a board view page
   * - The board view gives permission (canAutoShow)
   */
  const isBoardView = isBoardPath(
    new URL(windowUrl || window.location.href).pathname,
  );
  const oneTimeMessagesDismissed = data?.member?.oneTimeMessagesDismissed || [
    DISMISS_MESSAGE,
  ];
  const shouldShowPromptAutomatically = !!(
    boardAndMemberMeetRequirements &&
    canAutoShow &&
    isBoardView &&
    !oneTimeMessagesDismissed.includes(DISMISS_MESSAGE)
  );

  const [dismissPrompt] = useWorkspacesPreambleDismissPromptMutation();
  const onHide = (hideReason: HideReason) => {
    // If the user closes the popover by explicitly clicking the X button, dismiss the prompt
    if (hideReason === HideReason.CLICK_CLOSE_BUTTON) {
      dismissPrompt({
        variables: { messageId: `WorkspacesPreamblePrompt-${boardId}` },
      });
    }
  };

  const onShow = () => {
    Analytics.sendScreenEvent({
      name: 'workspacesPreambleAddToTeamInlineDialog',
      containers: {
        board: {
          id: boardId,
        },
      },
    });
    trackUe({
      category: Noun.BOARD_VIEW,
      verb: Verb.VIEWS,
      directObj: Noun.WORKSPACES_PREAMBLE_ADD_TO_TEAM_PROMPT,
      context: { boardId },
    });
  };

  const { popoverProps, push, show, toggle, triggerRef, hide } = usePopover<
    HTMLAnchorElement
  >({
    initialScreen: Screens.Intro,
    onHide,
    onShow,
  });

  const onButtonClick = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'workspacesPreambleAddToTeamButton',
      source: 'boardScreen',
      containers: {
        board: {
          id: boardId,
        },
      },
    });
    trackUe({
      category: Noun.BOARD_VIEW,
      verb: Verb.CLICKS,
      directObj: Noun.WORKSPACES_PREAMBLE_ADD_TO_TEAM_BUTTON,
      context: { boardId },
    });
    toggle();
  };

  useEffect(() => {
    if (shouldShowPromptAutomatically) {
      show();
    }
  }, [shouldShowPromptAutomatically, show]);

  useEffect(() => {
    if (!isTeamless) {
      hide();
    }
  }, [isTeamless, hide]);

  // Show an alert if we dismiss the popover due to another member adding it to a team
  const wasTeamless = usePrevious(isTeamless);
  useEffect(() => {
    if (wasTeamless && !isTeamless && popoverProps.isVisible) {
      Alerts.show('teamless-board-added-to-team', 'info', 'board', 5000);
    }
  }, [isTeamless, popoverProps.isVisible, wasTeamless]);

  const secondScreen = isMemberOfTeam ? Screens.SelectTeam : Screens.CreateTeam;

  // If the board and user do not meet the requirements for the WorkspacesPreamble feature, show the classic Personal button
  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-bizteam',
        feature: Feature.WorkspacesPreamble,
      }}
    >
      {boardAndMemberMeetRequirements ? (
        <>
          <a
            className="board-header-btn board-header-btn-without-icon"
            ref={triggerRef}
            onClick={onButtonClick}
            role="button"
          >
            <span className="board-header-btn-text">
              {formatPreamble('board-header-button')}
            </span>
          </a>
          <Popover
            {...popoverProps}
            title={
              isMemberOfTeam
                ? formatPreamble('popover-title-add-to-team')
                : formatPreamble('popover-title-create-team')
            }
          >
            <PopoverScreen id={Screens.Intro}>
              <div
                data-test-id="yo"
                className={styles.workspacesPreambleBoardHeaderButtonIntro}
              >
                <img
                  src={require('resources/images/workspaces-preamble/workspaces-preamble-prompt-board-image.png')}
                  alt=""
                />
                <h2
                  className={
                    styles.workspacesPreambleBoardHeaderButtonIntroTitle
                  }
                >
                  {formatPreamble('prompt-heading')}
                </h2>
                <p>{formatPreamble('prompt-paragraph')}</p>
                <Button
                  appearance="primary"
                  className={
                    styles.workspacesPreambleBoardHeaderButtonContinueButton
                  }
                  onClick={() => push(secondScreen)}
                  shouldFitContainer
                >
                  {isMemberOfTeam
                    ? formatPreamble('prompt-continue-button-add-to-team')
                    : formatPreamble('prompt-continue-button-create-team')}
                </Button>
              </div>
            </PopoverScreen>
            <PopoverScreen id={Screens.SelectTeam}>
              <WorkspacesPreambleSelectTeam
                analyticsDirectObj={Noun.WORKSPACES_PREAMBLE_ADD_TO_TEAM_PROMPT}
                analyticsSource="workspacesPreambleAddToTeamInlineDialog"
                boardId={boardId}
                onChooseCreateTeam={() => push(Screens.CreateTeam)}
                onSuccess={onSuccess}
              />
            </PopoverScreen>
            <PopoverScreen id={Screens.CreateTeam}>
              <WorkspacesPreambleCreateTeam
                analyticsDirectObj={Noun.WORKSPACES_PREAMBLE_ADD_TO_TEAM_PROMPT}
                analyticsSource="workspacesPreambleAddToTeamInlineDialog"
                boardId={boardId}
                onSuccess={onSuccess}
              />
            </PopoverScreen>
          </Popover>
        </>
      ) : (
        <a
          className={classNames(
            'board-header-btn board-header-btn-without-icon js-add-board-to-team',
            { 'no-edit': !isBoardAdmin },
          )}
        >
          <span className="board-header-btn-text">{format('personal')}</span>
        </a>
      )}
    </ErrorBoundary>
  );
};
